/**
 * AdminCommissionsPage — Overzicht alle commissies (FASE 3).
 *
 * Toont:
 *  - Stats: totaal pending / totaal paid / aantal
 *  - Filter op status (pending/paid/cancelled)
 *  - Per regel: ontvanger, klant, type (partner_fixed/affiliate_percentage), bedrag, status
 *  - Payout-knop met reference input (markeert paid)
 *  - Recompute-knop voor backfill
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Coins, RefreshCw, Loader2, Search, Filter, CheckCircle, Clock, XCircle, Calculator,
} from 'lucide-react';

const KIND_LABELS = {
  partner_fixed: { label: 'Callcenter (vast)', color: 'text-cyan-300 bg-cyan-500/10' },
  affiliate_percentage: { label: 'Affiliate (20%)', color: 'text-purple-300 bg-purple-500/10' },
};
const STATUS_COLORS = {
  pending: 'text-amber-400 bg-amber-500/10',
  paid: 'text-emerald-400 bg-emerald-500/10',
  cancelled: 'text-red-400 bg-red-500/10',
};

const formatEUR = (n) => `€${(n || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminCommissionsPage() {
  const { api } = useAuth();
  const [data, setData] = useState({ commissions: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [recomputing, setRecomputing] = useState(false);
  const [payoutFor, setPayoutFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', `/api/admin/commissions${statusFilter ? `?status=${statusFilter}` : ''}`);
      setData(r.data);
    } catch {
      toast.error('Commissies laden mislukt');
    }
    setLoading(false);
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setRecomputing(true);
    try {
      const r = await api('post', '/api/admin/commissions/recompute', {});
      toast.success(`Backfill: ${r.data.paid_transactions} transacties → ${r.data.commissions_created_or_existing} commissies`);
      load();
    } catch {
      toast.error('Backfill mislukt');
    }
    setRecomputing(false);
  };

  const filtered = data.commissions.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.recipient_name || '').toLowerCase().includes(q) ||
      (c.recipient_email || '').toLowerCase().includes(q) ||
      (c.klant_email || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">Admin</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Commissies
            </h1>
            <p className="text-white/60 text-sm mt-1">Callcenter €495,95 vast · Affiliate 20% van eerste maand</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={recompute} disabled={recomputing} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="recompute-commissions">
              {recomputing ? <Loader2 size={14} className="animate-spin" /> : <Calculator size={14} />} Backfill
            </button>
            <button onClick={load} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="refresh-commissions">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
          {[
            { label: 'Totaal regels', value: data.summary?.total_count || 0, color: 'text-white/70' },
            { label: 'Open (te betalen)', value: formatEUR(data.summary?.total_pending_ex_vat || 0), color: 'text-amber-400' },
            { label: 'Reeds uitbetaald', value: formatEUR(data.summary?.total_paid_ex_vat || 0), color: 'text-emerald-400' },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/5" data-testid={`commission-stat-${s.label.toLowerCase().replace(/[^a-z]/g, '-')}`}>
              <p className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op ontvanger of klant" className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40" data-testid="commission-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="commission-status-filter">
            <option value="">Alle</option>
            <option value="pending">Open</option>
            <option value="paid">Uitbetaald</option>
            <option value="cancelled">Geannuleerd</option>
          </select>
        </div>

        {loading ? (
          <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center text-white/40" data-testid="commissions-empty">Geen commissies gevonden.</div>
        ) : (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5">
                <tr className="text-left text-white/50 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Ontvanger</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Klant</th>
                  <th className="px-4 py-3 text-right">Bedrag</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Datum</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((c) => {
                  const kind = KIND_LABELS[c.kind] || { label: c.kind, color: 'text-white/60' };
                  const status = STATUS_COLORS[c.status] || '';
                  return (
                    <tr key={c.id} className="hover:bg-white/5" data-testid={`commission-row-${c.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-white">{c.recipient_name || c.recipient_email}</p>
                        <p className="text-white/40 text-xs">{c.recipient_role_resolved || c.recipient_role}</p>
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${kind.color}`}>{kind.label}</span></td>
                      <td className="px-4 py-3 text-white/70 text-xs">{c.klant_name || c.klant_email}</td>
                      <td className="px-4 py-3 text-right text-white font-bold">{formatEUR(c.amount_ex_vat)}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${status}`}>{c.status}</span></td>
                      <td className="px-4 py-3 text-white/50 text-xs">{c.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        {c.status === 'pending' && (
                          <button onClick={() => setPayoutFor(c)} className="text-emerald-400 hover:text-emerald-300 text-xs font-semibold" data-testid={`payout-${c.id}`}>
                            Markeer uitbetaald
                          </button>
                        )}
                        {c.status === 'paid' && c.paid_out_reference && (
                          <span className="text-emerald-400 text-xs font-mono">{c.paid_out_reference}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {payoutFor && <PayoutModal api={api} commission={payoutFor} onClose={() => setPayoutFor(null)} onPaid={() => { setPayoutFor(null); load(); }} />}
      </div>
    </div>
  );
}


function PayoutModal({ api, commission, onClose, onPaid }) {
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  const pay = async () => {
    setSaving(true);
    try {
      await api('post', `/api/admin/commissions/${commission.id}/payout`, { reference });
      toast.success('Commissie uitbetaald');
      onPaid();
    } catch {
      toast.error('Markeren mislukt');
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" data-testid="payout-modal">
        <h2 className="text-lg font-bold mb-3" style={{ fontFamily: "'Sora', sans-serif" }}>Commissie uitbetalen</h2>
        <p className="text-white/70 text-sm mb-3">
          <strong>{commission.recipient_name || commission.recipient_email}</strong><br />
          {formatEUR(commission.amount_ex_vat)} ex btw — {KIND_LABELS[commission.kind]?.label}
        </p>
        <label className="block mb-3">
          <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Betaalreferentie</span>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="bv. PAY-2026-001 of IBAN-overboeking" className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-xs" data-testid="payout-reference" />
        </label>
        <button onClick={pay} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2" data-testid="confirm-payout">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Bevestig uitbetaling
        </button>
      </div>
    </div>
  );
}
